import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SettingsModal from '../../../components/Game2048/ethSettingsModal';
import HamburgerButton from '../../../components/HamburgerButton';
import '../assets/2048.css';

export default function Wallet() {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [walletAddress, setWalletAddress] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    
    // USDC 餘額和交易相關狀態
    const [usdcBalance, setUsdcBalance] = useState('0.00');
    const [isLoadingBalance, setIsLoadingBalance] = useState(false);
    
    // Deposit 相關狀態
    const [depositAmount, setDepositAmount] = useState('');
    const [isDepositing, setIsDepositing] = useState(false);
    const [depositResult, setDepositResult] = useState(null);
    
    // Withdrawal 相關狀態
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [withdrawAddress, setWithdrawAddress] = useState('');
    const [isWithdrawing, setIsWithdrawing] = useState(false);
    const [withdrawResult, setWithdrawResult] = useState(null);
    
    // 交易歷史
    const [transactionHistory, setTransactionHistory] = useState([]);

    // USDC 合約地址 (以太坊主網 USDC 地址)
    const USDC_CONTRACT_ADDRESS = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
    const USDC_DECIMALS = 6; // USDC 使用 6 位小數

    // 檢查錢包連接狀態
    useEffect(() => {
        const checkWalletConnection = async () => {
            if (typeof window.ethereum !== 'undefined') {
                try {
                    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                    if (accounts.length > 0) {
                        const address = accounts[0];
                        setWalletAddress(address);
                        setWithdrawAddress(address); // 預設提款地址為當前地址
                        sessionStorage.setItem('zklogin_user_address', address);
                        setIsAuthenticated(true);
                        setUser({ address });
                        
                        // 獲取 USDC 餘額
                        fetchUSDCBalance(address);
                    } else {
                        setIsAuthenticated(false);
                    }
                } catch (error) {
                    console.error('檢查錢包連接失敗:', error);
                }
            }
        };

        checkWalletConnection();
    }, []);

    // 監聽錢包帳戶變化
    useEffect(() => {
        if (typeof window.ethereum !== 'undefined') {
            const handleAccountsChanged = (accounts) => {
                if (accounts.length === 0) {
                    setWalletAddress(null);
                    setIsAuthenticated(false);
                    sessionStorage.removeItem('zklogin_user_address');
                } else {
                    const address = accounts[0];
                    setWalletAddress(address);
                    setWithdrawAddress(address);
                    sessionStorage.setItem('zklogin_user_address', address);
                    setIsAuthenticated(true);
                    setUser({ address });
                    fetchUSDCBalance(address);
                }
            };

            window.ethereum.on('accountsChanged', handleAccountsChanged);

            return () => {
                if (window.ethereum && window.ethereum.removeListener) {
                    window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
                }
            };
        }
    }, []);

    // 獲取 USDC 餘額
    const fetchUSDCBalance = async (address) => {
        setIsLoadingBalance(true);
        try {
            // ERC20 balanceOf 函數的編碼
            const data = `0x70a08231000000000000000000000000${address.slice(2)}`;
            
            const balance = await window.ethereum.request({
                method: 'eth_call',
                params: [{
                    to: USDC_CONTRACT_ADDRESS,
                    data: data
                }, 'latest']
            });

            // 將 hex 轉換為數字並除以 10^6 (USDC decimals)
            const balanceInWei = parseInt(balance, 16);
            const balanceInUSDC = (balanceInWei / Math.pow(10, USDC_DECIMALS)).toFixed(2);
            
            setUsdcBalance(balanceInUSDC);
            console.log('✅ USDC 餘額:', balanceInUSDC);
        } catch (error) {
            console.error('❌ 獲取 USDC 餘額失敗:', error);
            setUsdcBalance('0.00');
        } finally {
            setIsLoadingBalance(false);
        }
    };

    // 處理 Deposit
    const handleDeposit = async () => {
        if (!depositAmount || parseFloat(depositAmount) <= 0) {
            alert('請輸入有效的存款金額');
            return;
        }

        if (!walletAddress) {
            alert('請先連接錢包');
            return;
        }

        setIsDepositing(true);
        setDepositResult(null);

        try {
            // 將金額轉換為 USDC 的最小單位
            const amountInSmallestUnit = (parseFloat(depositAmount) * Math.pow(10, USDC_DECIMALS)).toString(16);
            
            // ERC20 transfer 函數編碼
            // 這裡假設要轉到合約地址，實際應用中需要替換為您的智能合約地址
            const contractAddress = sessionStorage.getItem('blockchain_usdc_vault') || '0x0000000000000000000000000000000000000000';
            const transferData = `0xa9059cbb000000000000000000000000${contractAddress.slice(2)}${amountInSmallestUnit.padStart(64, '0')}`;

            const txHash = await window.ethereum.request({
                method: 'eth_sendTransaction',
                params: [{
                    from: walletAddress,
                    to: USDC_CONTRACT_ADDRESS,
                    data: transferData,
                    value: '0x0'
                }]
            });

            setDepositResult({
                success: true,
                txHash,
                amount: depositAmount
            });

            // 清空輸入並重新獲取餘額
            setDepositAmount('');
            setTimeout(() => {
                fetchUSDCBalance(walletAddress);
                addTransactionToHistory('Deposit', depositAmount, txHash);
            }, 2000);

            console.log('✅ 存款成功:', txHash);
        } catch (error) {
            console.error('❌ 存款失敗:', error);
            setDepositResult({
                success: false,
                error: error.message
            });
        } finally {
            setIsDepositing(false);
        }
    };

    // 處理 Withdrawal
    const handleWithdraw = async () => {
        if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) {
            alert('請輸入有效的提款金額');
            return;
        }

        if (!withdrawAddress || !withdrawAddress.match(/^0x[a-fA-F0-9]{40}$/)) {
            alert('請輸入有效的以太坊地址');
            return;
        }

        if (parseFloat(withdrawAmount) > parseFloat(usdcBalance)) {
            alert('餘額不足');
            return;
        }

        setIsWithdrawing(true);
        setWithdrawResult(null);

        try {
            // 將金額轉換為 USDC 的最小單位
            const amountInSmallestUnit = (parseFloat(withdrawAmount) * Math.pow(10, USDC_DECIMALS)).toString(16);
            
            // ERC20 transfer 函數編碼
            const transferData = `0xa9059cbb000000000000000000000000${withdrawAddress.slice(2)}${amountInSmallestUnit.padStart(64, '0')}`;

            const txHash = await window.ethereum.request({
                method: 'eth_sendTransaction',
                params: [{
                    from: walletAddress,
                    to: USDC_CONTRACT_ADDRESS,
                    data: transferData,
                    value: '0x0'
                }]
            });

            setWithdrawResult({
                success: true,
                txHash,
                amount: withdrawAmount,
                address: withdrawAddress
            });

            // 清空輸入並重新獲取餘額
            setWithdrawAmount('');
            setTimeout(() => {
                fetchUSDCBalance(walletAddress);
                addTransactionToHistory('Withdrawal', withdrawAmount, txHash);
            }, 2000);

            console.log('✅ 提款成功:', txHash);
        } catch (error) {
            console.error('❌ 提款失敗:', error);
            setWithdrawResult({
                success: false,
                error: error.message
            });
        } finally {
            setIsWithdrawing(false);
        }
    };

    // 添加交易到歷史記錄
    const addTransactionToHistory = (type, amount, txHash) => {
        const newTransaction = {
            type,
            amount,
            txHash,
            timestamp: new Date().toLocaleString(),
            status: 'Pending'
        };
        setTransactionHistory(prev => [newTransaction, ...prev].slice(0, 10)); // 只保留最近 10 筆
    };

    // 處理登入成功
    const handleLoginSuccess = (userData) => {
        setWalletAddress(userData.userAddress);
        setWithdrawAddress(userData.userAddress);
        setIsAuthenticated(true);
        setUser({ address: userData.userAddress });
        fetchUSDCBalance(userData.userAddress);
    };

    // 處理登出
    const handleLogout = () => {
        setWalletAddress(null);
        setIsAuthenticated(false);
        setUser(null);
        setUsdcBalance('0.00');
        setTransactionHistory([]);
    };

    // ESC 關閉 modal
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isMenuOpen) {
                setIsMenuOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isMenuOpen]);

    return (
        <div className="game-2048-container">
            {/* 頂部導航 */}
            <div className="top-bar">
                <h1 className="game-title">💰 USDC Wallet</h1>
                <HamburgerButton onClick={() => setIsMenuOpen(true)} />
            </div>

            {/* Settings Modal */}
            <SettingsModal
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                user={user}
                onLoginSuccess={handleLoginSuccess}
                onLoginError={(error) => console.error('Login error:', error)}
                onLogout={handleLogout}
                showBackButton={true}
                backButtonText="← Back to Game"
                onBackClick={() => navigate('/2048eth/game')}
            />

            {/* 主要內容 */}
            <div className="wallet-content">
                {!isAuthenticated ? (
                    <div className="wallet-auth-prompt">
                        <div className="auth-message">
                            <h2>🔐 Please Connect Your Wallet</h2>
                            <p>Click the menu button (☰) at the top right to connect your MetaMask wallet</p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* USDC 餘額顯示 */}
                        <div className="wallet-balance-card">
                            <h2 className="balance-title">💵 Your USDC Balance</h2>
                            <div className="balance-display">
                                {isLoadingBalance ? (
                                    <div className="loading-spinner"></div>
                                ) : (
                                    <div className="balance-amount">
                                        <span className="currency-symbol">$</span>
                                        <span className="amount">{usdcBalance}</span>
                                        <span className="currency-code">USDC</span>
                                    </div>
                                )}
                            </div>
                            <div className="wallet-address">
                                <small>Address: {walletAddress?.slice(0, 10)}...{walletAddress?.slice(-8)}</small>
                            </div>
                            <button 
                                className="refresh-balance-btn"
                                onClick={() => fetchUSDCBalance(walletAddress)}
                                disabled={isLoadingBalance}
                            >
                                🔄 Refresh Balance
                            </button>
                        </div>

                        {/* Deposit 區域 */}
                        <div className="wallet-action-card">
                            <h3 className="action-title">📥 Deposit USDC</h3>
                            <div className="action-form">
                                <div className="form-group">
                                    <label htmlFor="depositAmount">Amount (USDC)</label>
                                    <input
                                        id="depositAmount"
                                        type="number"
                                        className="wallet-input"
                                        placeholder="Enter amount to deposit"
                                        value={depositAmount}
                                        onChange={(e) => setDepositAmount(e.target.value)}
                                        min="0"
                                        step="0.01"
                                        disabled={isDepositing}
                                    />
                                </div>
                                <button
                                    className="wallet-action-btn deposit-btn"
                                    onClick={handleDeposit}
                                    disabled={isDepositing || !depositAmount}
                                >
                                    {isDepositing ? (
                                        <>
                                            <span className="loading-spinner"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        '💰 Deposit'
                                    )}
                                </button>
                                {depositResult && (
                                    <div className={`transaction-result ${depositResult.success ? 'success' : 'error'}`}>
                                        {depositResult.success ? (
                                            <>
                                                <strong>✅ Deposit Successful!</strong>
                                                <p>Amount: ${depositResult.amount} USDC</p>
                                                <p className="tx-hash">
                                                    Tx: <a 
                                                        href={`https://etherscan.io/tx/${depositResult.txHash}`} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                    >
                                                        {depositResult.txHash.slice(0, 10)}...
                                                    </a>
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <strong>❌ Deposit Failed</strong>
                                                <p>{depositResult.error}</p>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Withdrawal 區域 */}
                        <div className="wallet-action-card">
                            <h3 className="action-title">📤 Withdraw USDC</h3>
                            <div className="action-form">
                                <div className="form-group">
                                    <label htmlFor="withdrawAmount">Amount (USDC)</label>
                                    <input
                                        id="withdrawAmount"
                                        type="number"
                                        className="wallet-input"
                                        placeholder="Enter amount to withdraw"
                                        value={withdrawAmount}
                                        onChange={(e) => setWithdrawAmount(e.target.value)}
                                        min="0"
                                        step="0.01"
                                        disabled={isWithdrawing}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="withdrawAddress">Recipient Address</label>
                                    <input
                                        id="withdrawAddress"
                                        type="text"
                                        className="wallet-input"
                                        placeholder="0x..."
                                        value={withdrawAddress}
                                        onChange={(e) => setWithdrawAddress(e.target.value)}
                                        disabled={isWithdrawing}
                                    />
                                </div>
                                <button
                                    className="wallet-action-btn withdraw-btn"
                                    onClick={handleWithdraw}
                                    disabled={isWithdrawing || !withdrawAmount || !withdrawAddress}
                                >
                                    {isWithdrawing ? (
                                        <>
                                            <span className="loading-spinner"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        '💸 Withdraw'
                                    )}
                                </button>
                                {withdrawResult && (
                                    <div className={`transaction-result ${withdrawResult.success ? 'success' : 'error'}`}>
                                        {withdrawResult.success ? (
                                            <>
                                                <strong>✅ Withdrawal Successful!</strong>
                                                <p>Amount: ${withdrawResult.amount} USDC</p>
                                                <p>To: {withdrawResult.address.slice(0, 10)}...{withdrawResult.address.slice(-8)}</p>
                                                <p className="tx-hash">
                                                    Tx: <a 
                                                        href={`https://etherscan.io/tx/${withdrawResult.txHash}`} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                    >
                                                        {withdrawResult.txHash.slice(0, 10)}...
                                                    </a>
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <strong>❌ Withdrawal Failed</strong>
                                                <p>{withdrawResult.error}</p>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* 交易歷史 */}
                        {transactionHistory.length > 0 && (
                            <div className="wallet-history-card">
                                <h3 className="history-title">📜 Recent Transactions</h3>
                                <div className="transaction-list">
                                    {transactionHistory.map((tx, index) => (
                                        <div key={index} className="transaction-item">
                                            <div className="tx-type">
                                                {tx.type === 'Deposit' ? '📥' : '📤'} {tx.type}
                                            </div>
                                            <div className="tx-details">
                                                <div className="tx-amount">${tx.amount} USDC</div>
                                                <div className="tx-time">{tx.timestamp}</div>
                                                <a 
                                                    className="tx-link" 
                                                    href={`https://etherscan.io/tx/${tx.txHash}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View on Etherscan →
                                                </a>
                                            </div>
                                            <div className="tx-status">{tx.status}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* 樣式 */}
            <style>{`
                .game-2048-container {
                    font-family: "Clear Sans", "Helvetica Neue", Arial, sans-serif;
                    background: #faf8ef;
                    min-height: 100vh;
                    color: #776e65;
                }

                .top-bar {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    background: #eee4da;
                }

                .game-title {
                    font-size: 2em;
                    font-weight: bold;
                    color: #776e65;
                    margin: 0;
                }

                .wallet-content {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 20px;
                }

                /* 未登入提示 */
                .wallet-auth-prompt {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 60vh;
                }

                .auth-message {
                    text-align: center;
                    background: #eee4da;
                    padding: 40px;
                    border-radius: 12px;
                }

                .auth-message h2 {
                    color: #776e65;
                    margin-bottom: 15px;
                }

                .auth-message p {
                    color: #776e65;
                    opacity: 0.8;
                }

                /* 餘額卡片 */
                .wallet-balance-card {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border-radius: 16px;
                    padding: 30px;
                    margin-bottom: 30px;
                    color: white;
                    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
                    text-align: center;
                }

                .balance-title {
                    font-size: 1.2em;
                    margin-bottom: 20px;
                    opacity: 0.9;
                }

                .balance-display {
                    margin: 20px 0;
                }

                .balance-amount {
                    font-size: 3em;
                    font-weight: bold;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                }

                .currency-symbol {
                    font-size: 0.7em;
                    opacity: 0.8;
                }

                .amount {
                    letter-spacing: -0.02em;
                }

                .currency-code {
                    font-size: 0.4em;
                    opacity: 0.8;
                }

                .wallet-address {
                    margin-top: 15px;
                    opacity: 0.8;
                    font-family: monospace;
                }

                .refresh-balance-btn {
                    margin-top: 20px;
                    background: rgba(255, 255, 255, 0.2);
                    border: 1px solid rgba(255, 255, 255, 0.3);
                    color: white;
                    padding: 10px 20px;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                .refresh-balance-btn:hover:not(:disabled) {
                    background: rgba(255, 255, 255, 0.3);
                    transform: translateY(-1px);
                }

                .refresh-balance-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                /* 操作卡片 */
                .wallet-action-card {
                    background: #eee4da;
                    border-radius: 12px;
                    padding: 25px;
                    margin-bottom: 20px;
                }

                .action-title {
                    color: #776e65;
                    margin-bottom: 20px;
                    font-size: 1.3em;
                }

                .action-form {
                    display: flex;
                    flex-direction: column;
                    gap: 15px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .form-group label {
                    font-weight: 600;
                    color: #776e65;
                    font-size: 0.9em;
                }

                .wallet-input {
                    padding: 12px;
                    border: 2px solid #ccc4b8;
                    border-radius: 8px;
                    font-size: 1em;
                    background: #fff;
                    color: #776e65;
                    transition: border-color 0.3s ease;
                }

                .wallet-input:focus {
                    outline: none;
                    border-color: #8f7a66;
                }

                .wallet-input:disabled {
                    background: #f0ebe4;
                    cursor: not-allowed;
                }

                .wallet-action-btn {
                    padding: 14px 20px;
                    border: none;
                    border-radius: 8px;
                    font-size: 1em;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                }

                .deposit-btn {
                    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
                    color: white;
                    box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
                }

                .deposit-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #20c997 0%, #17a2b8 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
                }

                .withdraw-btn {
                    background: linear-gradient(135deg, #fd7e14 0%, #dc3545 100%);
                    color: white;
                    box-shadow: 0 4px 15px rgba(253, 126, 20, 0.3);
                }

                .withdraw-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(253, 126, 20, 0.4);
                }

                .wallet-action-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    transform: none;
                }

                /* 交易結果 */
                .transaction-result {
                    padding: 15px;
                    border-radius: 8px;
                    margin-top: 10px;
                }

                .transaction-result.success {
                    background: #d4edda;
                    border: 1px solid #c3e6cb;
                    color: #155724;
                }

                .transaction-result.error {
                    background: #f8d7da;
                    border: 1px solid #f5c6cb;
                    color: #721c24;
                }

                .transaction-result strong {
                    display: block;
                    margin-bottom: 8px;
                }

                .transaction-result p {
                    margin: 5px 0;
                    font-size: 0.9em;
                }

                .tx-hash a {
                    color: inherit;
                    text-decoration: underline;
                }

                /* 交易歷史 */
                .wallet-history-card {
                    background: #eee4da;
                    border-radius: 12px;
                    padding: 25px;
                    margin-bottom: 20px;
                }

                .history-title {
                    color: #776e65;
                    margin-bottom: 20px;
                    font-size: 1.3em;
                }

                .transaction-list {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .transaction-item {
                    background: #fff;
                    border-radius: 8px;
                    padding: 15px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }

                .tx-type {
                    font-weight: 600;
                    color: #776e65;
                    min-width: 120px;
                }

                .tx-details {
                    flex: 1;
                    padding: 0 15px;
                }

                .tx-amount {
                    font-weight: 600;
                    color: #776e65;
                    margin-bottom: 5px;
                }

                .tx-time {
                    font-size: 0.8em;
                    color: #999;
                    margin-bottom: 5px;
                }

                .tx-link {
                    font-size: 0.85em;
                    color: #667eea;
                    text-decoration: none;
                }

                .tx-link:hover {
                    text-decoration: underline;
                }

                .tx-status {
                    background: #fff3cd;
                    color: #856404;
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 0.85em;
                    font-weight: 600;
                }

                /* Loading Spinner */
                .loading-spinner {
                    width: 20px;
                    height: 20px;
                    border: 3px solid rgba(255, 255, 255, 0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    display: inline-block;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                /* 響應式設計 */
                @media (max-width: 768px) {
                    .game-title {
                        font-size: 1.5em;
                    }

                    .balance-amount {
                        font-size: 2em;
                    }

                    .wallet-action-card,
                    .wallet-balance-card,
                    .wallet-history-card {
                        padding: 20px;
                    }

                    .transaction-item {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 10px;
                    }

                    .tx-type,
                    .tx-details {
                        width: 100%;
                        padding: 0;
                    }

                    .tx-status {
                        align-self: flex-end;
                    }
                }
            `}</style>
        </div>
    );
}
